// skills/architecting/architecting.js
// Architecting skill logic

import { BLUEPRINTS, TOOLS, MILESTONES, CIVIC_CONTRACTS, CONTRACT_SPAWN_CONFIG, getBlueprintsByTier } from './architectingData.js';
import { gameState } from '../../game/gameState.js';
import { addXP } from '../../systems/experience.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { rollForCorruption } from '../../systems/corruption.js';
import { getItem, getItemName } from '../../data/items/index.js';
import { getTalentFlatBonus, getTalentProcChance, rollTalentProc, onTalentUnlocked } from '../../systems/talentTree/talentTree.js';
import { recordTalentProc } from '../../ui/talentActivity.js';
import { BaseSkill } from '../../core/BaseSkill.js';

class ArchitectingSkill extends BaseSkill {
    constructor() {
        super(SKILLS.ARCHITECTING, TOOLS, 'PYRESTONE');
        this.onBlueprintComplete = null;
        this.onMilestoneComplete = null;
        this.onCivicPhaseComplete = null;
        this.onQuickBuildComplete = null;
    }

    /**
     * Get the currently equipped tool object (alias for BaseSkill method)
     */
    get currentTool() {
        return this.getCurrentTool();
    }

    /**
     * Get the blueprint speed bonus from milestone buildings
     */
    getBlueprintSpeedBonus() {
        let bonus = 0;
        if (gameState.hasMilestone('blueprint_studio_i')) {
            bonus += 0.10; // 10% from Studio I
        }
        if (gameState.hasMilestone('blueprint_studio_ii')) {
            bonus += 0.15; // 15% from Studio II
        }
        return bonus;
    }

    // ==================== CONTRACTOR METHODS ====================

    /**
     * Check if the contractor has been hired (quest unlock)
     */
    hasContractor() {
        return gameState.hasQuestUnlock('contractor_hired');
    }

    /**
     * Get the number of contractor slots available (1 base, 2 with Apprentice Contractor talent)
     */
    getContractorSlotCount() {
        const hasDualTalent = getTalentFlatBonus(SKILLS.ARCHITECTING, 'dualBlueprint') > 0;
        return hasDualTalent ? 2 : 1;
    }

    /**
     * Get the number of contractor slots currently in use (blueprints + milestone)
     */
    getUsedContractorSlots() {
        const activeBlueprints = gameState.getActiveBlueprints();
        const activeMilestone = gameState.getActiveMilestone();
        const activeCivic = gameState.getActiveCivicContract();
        const civicUsingSlot = activeCivic && activeCivic.status === 'building' ? 1 : 0;
        return activeBlueprints.length + (activeMilestone ? 1 : 0) + civicUsingSlot;
    }

    // ==================== ACTIVE QUICK BUILD METHODS ====================

    /**
     * Check if a quick build (active action) can be started
     */
    canQuickBuild(blueprintId) {
        const blueprint = Object.values(BLUEPRINTS).find(b => b.id === blueprintId);
        if (!blueprint) return { success: false, reason: 'Blueprint not found' };
        if (blueprint.buildMode !== 'active') return { success: false, reason: 'Not an active blueprint' };

        const skill = gameState.getSkill(SKILLS.ARCHITECTING);
        if (skill.level < blueprint.level) {
            return { success: false, reason: `Requires level ${blueprint.level} Architecting` };
        }

        // Check material requirements
        const missingMaterials = [];
        for (const [itemId, qty] of Object.entries(blueprint.materials)) {
            const playerQty = gameState.getItemCount(itemId);
            if (playerQty < qty) {
                const item = getItem(itemId);
                const itemName = item ? item.name : itemId;
                missingMaterials.push(`${itemName}: ${playerQty}/${qty}`);
            }
        }

        if (missingMaterials.length > 0) {
            return { success: false, reason: `Missing: ${missingMaterials.join(', ')}` };
        }

        return { success: true };
    }

    /**
     * Start an active quick build (uses the standard action system)
     */
    startQuickBuild(blueprintId) {
        const blueprint = Object.values(BLUEPRINTS).find(b => b.id === blueprintId);
        const canBuild = this.canQuickBuild(blueprintId);

        if (!canBuild.success) {
            return canBuild;
        }

        // Calculate duration with tool speed
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const duration = blueprint.baseTime / speedMultiplier;

        // Set as standard action (shows in sidebar, has progress bar)
        const action = {
            type: 'architecting',
            buildMode: 'active',
            blueprintId: blueprint.id,
            blueprint: blueprint,
            startTime: Date.now(),
            duration: duration,
            progress: 0,
            tick: (adminSpeedMult = 1) => {
                const currentAction = gameState.player.currentAction;
                if (!currentAction || currentAction.type !== 'architecting') return;

                // Handle force complete (admin panel)
                if (currentAction.forceComplete) {
                    currentAction.forceComplete = false;
                    this.completeQuickBuild(blueprint);
                    const canContinue = this.canQuickBuild(blueprintId);
                    if (canContinue.success) {
                        this.startQuickBuild(blueprintId);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                // Update progress
                const elapsed = (Date.now() - currentAction.startTime) * adminSpeedMult;
                currentAction.progress = Math.min(elapsed / currentAction.duration, 1);

                // Complete when done
                if (currentAction.progress >= 1) {
                    this.completeQuickBuild(blueprint);
                    const canContinue = this.canQuickBuild(blueprintId);
                    if (canContinue.success) {
                        this.startQuickBuild(blueprintId);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        logger.debug(`Started quick build: ${blueprint.name} (${duration}ms)`);
        return { success: true };
    }

    /**
     * Complete an active quick build
     */
    completeQuickBuild(blueprint) {
        // Consume materials on completion (matches other skill patterns)
        for (const [itemId, qty] of Object.entries(blueprint.materials)) {
            gameState.removeItem(itemId, qty);
        }

        const xpResult = addXP(SKILLS.ARCHITECTING, blueprint.xp, blueprint.id);

        // Roll for corruption (still possible on active builds)
        rollForCorruption(SKILLS.ARCHITECTING);

        // No civic contract roll for active builds (too frequent at 30s)

        if (this.onQuickBuildComplete) {
            this.onQuickBuildComplete(blueprint, xpResult);
        }

        logger.game(`Finished a ${blueprint.name}. It wobbles, but it stands... barely. +${blueprint.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Architecting level up! Now level ${xpResult.newLevel}`);
        }
    }

    // ==================== PASSIVE BLUEPRINT METHODS ====================

    /**
     * Check if a blueprint can be built
     */
    canBuild(blueprintId) {
        const blueprint = Object.values(BLUEPRINTS).find(b => b.id === blueprintId);
        if (!blueprint) return { success: false, reason: 'Blueprint not found' };

        const skill = gameState.getSkill(SKILLS.ARCHITECTING);
        if (skill.level < blueprint.level) {
            return { success: false, reason: `Requires level ${blueprint.level} Architecting` };
        }

        // Check gold requirement
        if (blueprint.gold > 0) {
            const playerGold = gameState.getGold();
            if (playerGold < blueprint.gold) {
                return { success: false, reason: `Need ${blueprint.gold} gold (have ${playerGold})` };
            }
        }

        // Check material requirements
        const missingMaterials = [];
        for (const [itemId, qty] of Object.entries(blueprint.materials)) {
            const playerQty = gameState.getItemCount(itemId);
            if (playerQty < qty) {
                const item = getItem(itemId);
                const itemName = item ? item.name : itemId;
                missingMaterials.push(`${itemName}: ${playerQty}/${qty}`);
            }
        }

        if (missingMaterials.length > 0) {
            return { success: false, reason: `Missing: ${missingMaterials.join(', ')}` };
        }

        return { success: true };
    }

    /**
     * Consume materials for a blueprint
     */
    consumeMaterials(blueprint) {
        // Consume gold
        if (blueprint.gold > 0) {
            gameState.removeItem('coins', blueprint.gold);
        }

        // Consume materials
        for (const [itemId, qty] of Object.entries(blueprint.materials)) {
            gameState.removeItem(itemId, qty);
        }
    }

    /**
     * Start building a blueprint (passive - runs in background, requires contractor)
     */
    startBuilding(blueprintId) {
        // Contractor required for passive builds
        if (!this.hasContractor()) {
            return { success: false, reason: 'Hire a contractor first (complete "The Carpenter\'s Arrival" quest)' };
        }

        const blueprint = Object.values(BLUEPRINTS).find(b => b.id === blueprintId);
        if (blueprint?.buildMode === 'active') {
            return { success: false, reason: 'Use quick build for this blueprint' };
        }

        const canBuild = this.canBuild(blueprintId);

        if (!canBuild.success) {
            logger.debug('Cannot build:', canBuild.reason);
            return canBuild;
        }

        const activeBlueprints = gameState.getActiveBlueprints();
        const maxSlots = this.getContractorSlotCount();
        const usedSlots = this.getUsedContractorSlots();

        // Check shared contractor slot availability
        if (usedSlots >= maxSlots) {
            const activeMilestone = gameState.getActiveMilestone();
            if (activeMilestone && maxSlots === 1) {
                return { success: false, reason: 'Contractor is building a milestone. Stop it first or unlock Apprentice Contractor.' };
            }
            if (activeBlueprints.length >= maxSlots) {
                return { success: false, reason: maxSlots === 1 ? 'Already building a blueprint' : 'Both contractor slots are full' };
            }
            return { success: false, reason: 'All contractor slots are in use' };
        }

        // Don't allow same blueprint in both slots
        if (activeBlueprints.length === 1 && activeBlueprints[0].blueprintId === blueprintId) {
            return { success: false, reason: 'Already building this blueprint' };
        }

        // Consume materials upfront
        this.consumeMaterials(blueprint);

        // Calculate building time (speed penalty applied if this will be the 2nd contractor task)
        const willBeDual = usedSlots === 1;
        const duration = this.calculateBuildTime(blueprint, willBeDual);

        // Start passive blueprint (stored in gameState, not currentAction)
        gameState.startBlueprint(blueprintId, duration);

        // If adding a 2nd task, apply penalty to existing tasks
        if (willBeDual) {
            if (activeBlueprints.length === 1) {
                this.applyDualPenaltyToExisting(0);
            }
            // Also apply penalty to milestone if one is running
            this.applyDualPenaltyToMilestone();
        }

        logger.debug(`Started building ${blueprint.name} (${duration}ms)`);
        return { success: true };
    }

    /**
     * Apply dual speed penalty to an existing blueprint slot when a 2nd blueprint starts
     * Adjusts startedAt so remaining time reflects the 75% speed
     */
    applyDualPenaltyToExisting(slotIndex) {
        const blueprints = gameState.getActiveBlueprints();
        const existing = blueprints[slotIndex];
        if (!existing) return;

        const now = Date.now();
        const elapsed = now - existing.startedAt;
        const originalDuration = existing.duration;

        // How much progress has been made at full speed
        const progressMade = Math.min(elapsed / originalDuration, 1);
        if (progressMade >= 1) return; // Already complete, will be handled by tick

        // Recalculate: remaining portion now takes 33% longer (1/0.75)
        const newDuration = originalDuration / 0.75; // Stretch full duration
        // Adjust startedAt so current progress is preserved
        existing.startedAt = now - (progressMade * newDuration);
        existing.duration = newDuration;
    }

    /**
     * Remove dual speed penalty from remaining blueprint when other slot finishes
     * Adjusts timing back to full speed
     */
    removeDualPenaltyFromRemaining(slotIndex) {
        const blueprints = gameState.getActiveBlueprints();
        const remaining = blueprints[slotIndex];
        if (!remaining) return;

        const now = Date.now();
        const elapsed = now - remaining.startedAt;
        const currentDuration = remaining.duration;

        const progressMade = Math.min(elapsed / currentDuration, 1);
        if (progressMade >= 1) return;

        // Recalculate: get the original (non-penalized) duration
        const originalDuration = currentDuration * 0.75; // Shrink back
        // Adjust startedAt so current progress is preserved at new speed
        remaining.startedAt = now - (progressMade * originalDuration);
        remaining.duration = originalDuration;
    }

    /**
     * Apply dual speed penalty to the active milestone when a 2nd contractor task starts
     */
    applyDualPenaltyToMilestone() {
        const milestone = gameState.getActiveMilestone();
        if (!milestone) return;

        const now = Date.now();
        const elapsed = now - milestone.startedAt;
        const originalDuration = milestone.duration;

        const progressMade = Math.min(elapsed / originalDuration, 1);
        if (progressMade >= 1) return;

        const newDuration = originalDuration / 0.75;
        milestone.startedAt = now - (progressMade * newDuration);
        milestone.duration = newDuration;
    }

    /**
     * Remove dual speed penalty from the active milestone when other task finishes
     */
    removeDualPenaltyFromMilestone() {
        const milestone = gameState.getActiveMilestone();
        if (!milestone) return;

        const now = Date.now();
        const elapsed = now - milestone.startedAt;
        const currentDuration = milestone.duration;

        const progressMade = Math.min(elapsed / currentDuration, 1);
        if (progressMade >= 1) return;

        const originalDuration = currentDuration * 0.75;
        milestone.startedAt = now - (progressMade * originalDuration);
        milestone.duration = originalDuration;
    }

    /**
     * Calculate build time for a blueprint
     * @param {Object} blueprint - Blueprint data
     * @param {boolean} isDual - Whether this will run alongside another contractor task (75% speed)
     */
    calculateBuildTime(blueprint, isDual = false) {
        const baseTime = blueprint.baseTime;
        // Get base speed multiplier (tool + mastery + admin + talent speed)
        let finalMultiplier = this.getEffectiveSpeedMultiplier();
        // Apply blueprint studio bonus (additive with other bonuses)
        const blueprintSpeedBonus = this.getBlueprintSpeedBonus();
        if (blueprintSpeedBonus > 0) {
            finalMultiplier *= (1 + blueprintSpeedBonus);
        }
        // Apply dual blueprint penalty (75% speed when both slots active)
        if (isDual) {
            finalMultiplier *= 0.75;
        }
        return baseTime / finalMultiplier;
    }

    /**
     * Stop a blueprint by slot index
     * @param {number} index - Slot index (0 or 1)
     */
    stopBuilding(index = 0) {
        const blueprints = gameState.getActiveBlueprints();
        const usedSlots = this.getUsedContractorSlots();

        // If going from 2 contractor tasks to 1, remove penalty from remaining tasks
        if (usedSlots === 2) {
            // Remove penalty from other blueprint if two blueprints running
            if (blueprints.length === 2) {
                const otherIndex = index === 0 ? 1 : 0;
                this.removeDualPenaltyFromRemaining(otherIndex);
            }
            // Remove penalty from milestone if running alongside blueprint
            this.removeDualPenaltyFromMilestone();
        }

        gameState.stopBlueprint(index);
    }

    /**
     * Tick blueprints (called from main tick loop - passive like husbandry)
     * Loops through all active blueprints (1-2 slots)
     */
    tickBlueprint() {
        const activeBlueprints = gameState.getActiveBlueprints();
        if (activeBlueprints.length === 0) return;

        // Collect completed blueprints first, then process
        const completedSlots = [];
        for (let i = activeBlueprints.length - 1; i >= 0; i--) {
            const progress = gameState.getBlueprintProgress(i);
            if (progress >= 1) {
                completedSlots.push({ index: i, blueprintId: activeBlueprints[i].blueprintId });
            }
        }

        if (completedSlots.length === 0) return;

        // If going from 2 contractor tasks to fewer, remove penalty from remaining
        const usedSlots = this.getUsedContractorSlots();
        if (usedSlots === 2 && completedSlots.length >= 1) {
            // Remove penalty from remaining blueprint if two blueprints were running
            if (activeBlueprints.length === 2 && completedSlots.length === 1) {
                const otherIndex = completedSlots[0].index === 0 ? 1 : 0;
                this.removeDualPenaltyFromRemaining(otherIndex);
            }
            // Remove penalty from milestone if running alongside blueprint
            this.removeDualPenaltyFromMilestone();
        }

        // Process completions: complete then stop (in reverse order to preserve indices)
        const completedTiers = [];
        for (const slot of completedSlots) {
            const blueprintData = Object.values(BLUEPRINTS).find(b => b.id === slot.blueprintId);
            if (blueprintData) {
                this.completeBlueprint(blueprintData, slot.index);
                completedTiers.push(blueprintData.tier);
            }
            gameState.stopBlueprint(slot.index);
        }

        // Auto-queue after stops are processed (so slot is available)
        const hasAutoQueue = getTalentFlatBonus(SKILLS.ARCHITECTING, 'autoQueueBlueprint') > 0;
        if (hasAutoQueue && gameState.getAutoQueueEnabled()) {
            for (const tier of completedTiers) {
                this.tryAutoQueueBlueprint(tier);
            }
        }
    }

    /**
     * Complete a blueprint
     * @param {Object} blueprint - Blueprint data
     * @param {number} slotIndex - Which slot completed (0 or 1)
     */
    completeBlueprint(blueprint, slotIndex = 0) {
        const xpResult = addXP(SKILLS.ARCHITECTING, blueprint.xp, blueprint.id);

        // Roll for corruption proc
        rollForCorruption(SKILLS.ARCHITECTING);

        // Roll for civic contract spawn
        this.rollForCivicContract();

        // Talent: Material Mastery - chance to refund materials
        if (rollTalentProc(SKILLS.ARCHITECTING, 'preserveMaterials')) {
            // Refund all materials used
            for (const [itemId, qty] of Object.entries(blueprint.materials)) {
                gameState.addItem(itemId, qty);
            }
            recordTalentProc(SKILLS.ARCHITECTING, 'Material Mastery', 'Materials preserved!', null);
        }

        if (this.onBlueprintComplete) {
            this.onBlueprintComplete(blueprint, xpResult);
        }

        logger.game(`Built ${blueprint.name}! +${blueprint.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Architecting level up! Now level ${xpResult.newLevel}`);
        }
    }

    /**
     * Try to auto-queue the next blueprint of the same tier (from Auto-Queue talent)
     */
    tryAutoQueueBlueprint(tier) {
        const availableBlueprints = this.getAvailableBlueprintsByTier(tier);

        // Find a blueprint we can afford
        for (const nextBlueprint of availableBlueprints) {
            const canBuild = this.canBuild(nextBlueprint.id);
            if (canBuild.success) {
                this.startBuilding(nextBlueprint.id);
                logger.game(`Auto-Queue: Started building ${nextBlueprint.name}`);
                return true;
            }
        }

        // No affordable blueprints in this tier, try lower tiers
        for (let t = tier - 1; t >= 1; t--) {
            const lowerTierBlueprints = this.getAvailableBlueprintsByTier(t);
            for (const nextBlueprint of lowerTierBlueprints) {
                const canBuild = this.canBuild(nextBlueprint.id);
                if (canBuild.success) {
                    this.startBuilding(nextBlueprint.id);
                    logger.game(`Auto-Queue: Started building ${nextBlueprint.name} (lower tier)`);
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Roll for a civic contract spawn after blueprint completion
     * Chance increases with architecting level
     * @param {boolean} forceSpawn - If true, skip RNG roll (used for milestone completion)
     */
    rollForCivicContract(forceSpawn = false) {
        const skill = gameState.getSkill(SKILLS.ARCHITECTING);
        const level = skill.level;

        // Skip RNG roll if forced (milestone completion guarantee)
        if (!forceSpawn) {
            // Calculate spawn chance based on level
            const levelBonus = Math.floor(level / 10) * CONTRACT_SPAWN_CONFIG.CHANCE_PER_10_LEVELS;
            // Talent: Trusted Builder - bonus spawn chance
            const talentSpawnBonus = getTalentFlatBonus(SKILLS.ARCHITECTING, 'contractSpawnBonus');
            const spawnChance = Math.min(
                CONTRACT_SPAWN_CONFIG.BASE_CHANCE + levelBonus + talentSpawnBonus,
                CONTRACT_SPAWN_CONFIG.MAX_CHANCE + talentSpawnBonus // Allow talents to exceed base max
            );

            // Roll for spawn
            if (Math.random() >= spawnChance) {
                return null; // No spawn
            }
        }

        // Already have an active contract or max available contracts
        const availableContracts = gameState.getAvailableCivicContracts();
        const activeContract = gameState.getActiveCivicContract();
        if (activeContract || availableContracts.length >= 3) {
            return null; // Cap at 3 available contracts
        }

        // Get eligible contracts based on level (not already available or completed)
        const completedContracts = gameState.getCompletedCivicContracts();
        const eligibleContracts = Object.values(CIVIC_CONTRACTS).filter(contract => {
            // Must meet level requirement
            if (level < contract.level) return false;
            // Not already available
            if (availableContracts.includes(contract.id)) return false;
            // Not currently active
            if (activeContract && activeContract.contractId === contract.id) return false;
            // Note: We allow re-doing completed contracts
            return true;
        });

        if (eligibleContracts.length === 0) {
            return null;
        }

        // Weight by rarity (uncommon more common than rare, rare more common than legendary)
        // Grand Architect capstone: no uncommon contracts, boosted legendary chance
        const hasRarityBoost = getTalentFlatBonus(SKILLS.ARCHITECTING, 'contractRarityBoost') > 0;
        const rarityWeights = hasRarityBoost
            ? { uncommon: 0, rare: 60, legendary: 40 }  // No uncommon, 40% legendary
            : { uncommon: 60, rare: 30, legendary: 10 }; // Normal weights

        const weightedContracts = [];
        for (const contract of eligibleContracts) {
            const weight = rarityWeights[contract.rarity] || 30;
            if (weight > 0) {
                for (let i = 0; i < weight; i++) {
                    weightedContracts.push(contract);
                }
            }
        }

        // If no eligible contracts after filtering (e.g., only uncommon available but rarity boosted)
        if (weightedContracts.length === 0) {
            return null;
        }

        // Pick a random contract
        const selectedContract = weightedContracts[Math.floor(Math.random() * weightedContracts.length)];

        // Add to available contracts
        gameState.addAvailableCivicContract(selectedContract.id);
        logger.game(`A civic contract appeared: ${selectedContract.name}!`);

        return selectedContract;
    }

    /**
     * Purchase or equip a tool
     */
    upgradeTool(toolId) {
        const tool = Object.values(TOOLS).find(t => t.id === toolId);
        if (!tool) return { success: false, reason: 'Invalid tool' };

        const skill = gameState.getSkill(SKILLS.ARCHITECTING);
        if (skill.level < tool.level) {
            return { success: false, reason: `Requires level ${tool.level} Architecting` };
        }

        if (gameState.hasPurchasedTool(SKILLS.ARCHITECTING, toolId)) {
            gameState.equipTool(SKILLS.ARCHITECTING, toolId);
            return { success: true, action: 'equipped' };
        }

        const purchaseResult = gameState.purchaseTool(SKILLS.ARCHITECTING, toolId, tool.price);
        if (!purchaseResult.success) {
            return purchaseResult;
        }

        gameState.equipTool(SKILLS.ARCHITECTING, toolId);
        return { success: true, action: 'purchased' };
    }

    /**
     * Get blueprints available at current level
     */
    getAvailableBlueprints() {
        const skill = gameState.getSkill(SKILLS.ARCHITECTING);
        return Object.values(BLUEPRINTS).filter(blueprint => blueprint.level <= skill.level && blueprint.buildMode !== 'active');
    }

    /**
     * Get blueprints by tier that are available at current level
     */
    getAvailableBlueprintsByTier(tier) {
        const skill = gameState.getSkill(SKILLS.ARCHITECTING);
        return getBlueprintsByTier(tier).filter(blueprint => blueprint.level <= skill.level && blueprint.buildMode !== 'active');
    }

    // ==================== MILESTONE METHODS ====================

    /**
     * Check if a milestone can be started
     */
    canStartMilestone(milestoneId) {
        const milestone = Object.values(MILESTONES).find(m => m.id === milestoneId);
        if (!milestone) return { success: false, reason: 'Milestone not found' };

        // Contractor required for milestones
        if (!this.hasContractor()) {
            return { success: false, reason: 'Hire a contractor first (complete "The Carpenter\'s Arrival" quest)' };
        }

        // Already completed?
        if (gameState.hasMilestone(milestoneId)) {
            return { success: false, reason: 'Already completed' };
        }

        // Already building a milestone?
        if (gameState.getActiveMilestone()) {
            return { success: false, reason: 'Already building a milestone' };
        }

        // Check shared contractor slot availability
        const usedSlots = this.getUsedContractorSlots();
        const maxSlots = this.getContractorSlotCount();
        if (usedSlots >= maxSlots) {
            return { success: false, reason: 'All contractor slots are in use', needsConfirmation: usedSlots === 1 };
        }

        const skill = gameState.getSkill(SKILLS.ARCHITECTING);
        if (skill.level < milestone.level) {
            return { success: false, reason: `Requires level ${milestone.level} Architecting` };
        }

        // Check secondary skill requirement (for tier upgrade milestones)
        if (milestone.secondarySkill && milestone.secondaryLevel) {
            const secondarySkill = gameState.getSkill(milestone.secondarySkill);
            const secondaryLevel = secondarySkill ? secondarySkill.level : 1;
            if (secondaryLevel < milestone.secondaryLevel) {
                // Get skill name from registry
                const skillInfo = Object.entries(SKILLS).find(([key, val]) => val === milestone.secondarySkill);
                const skillName = skillInfo ? skillInfo[0].charAt(0) + skillInfo[0].slice(1).toLowerCase() : 'skill';
                return { success: false, reason: `Requires level ${milestone.secondaryLevel} ${skillName}` };
            }
        }

        // Check single prerequisite (legacy)
        if (milestone.prerequisite && !gameState.hasMilestone(milestone.prerequisite)) {
            const prereq = Object.values(MILESTONES).find(m => m.id === milestone.prerequisite);
            const prereqName = prereq ? prereq.name : milestone.prerequisite;
            return { success: false, reason: `Requires: ${prereqName}` };
        }

        // Check multiple prerequisites (for Architect's Sanctum)
        if (milestone.prerequisites && milestone.prerequisites.length > 0) {
            for (const prereqId of milestone.prerequisites) {
                if (!gameState.hasMilestone(prereqId)) {
                    const prereq = Object.values(MILESTONES).find(m => m.id === prereqId);
                    const prereqName = prereq ? prereq.name : prereqId;
                    return { success: false, reason: `Requires: ${prereqName}` };
                }
            }
        }

        // Check gold
        if (milestone.gold > 0) {
            const playerGold = gameState.getGold();
            if (playerGold < milestone.gold) {
                return { success: false, reason: `Need ${milestone.gold} gold (have ${playerGold})` };
            }
        }

        // Check materials
        const missingMaterials = [];
        for (const [itemId, qty] of Object.entries(milestone.materials)) {
            const playerQty = gameState.getItemCount(itemId);
            if (playerQty < qty) {
                const item = getItem(itemId);
                const itemName = item ? item.name : itemId;
                missingMaterials.push(`${itemName}: ${playerQty}/${qty}`);
            }
        }

        if (missingMaterials.length > 0) {
            return { success: false, reason: `Missing: ${missingMaterials.join(', ')}` };
        }

        return { success: true };
    }

    /**
     * Start building a milestone (passive - runs alongside other actions)
     */
    startMilestone(milestoneId) {
        const milestone = Object.values(MILESTONES).find(m => m.id === milestoneId);
        const canStart = this.canStartMilestone(milestoneId);

        if (!canStart.success) {
            return canStart;
        }

        // Consume gold
        if (milestone.gold > 0) {
            gameState.removeItem('coins', milestone.gold);
        }

        // Consume materials
        for (const [itemId, qty] of Object.entries(milestone.materials)) {
            gameState.removeItem(itemId, qty);
        }

        // Check if this will be a dual task (blueprint already running)
        const usedSlots = this.getUsedContractorSlots();
        const willBeDual = usedSlots === 1;
        const dualPenalty = willBeDual ? 0.75 : 1;

        // Set active milestone
        gameState.setActiveMilestone({
            milestoneId: milestoneId,
            startedAt: Date.now(),
            duration: milestone.buildTime / dualPenalty // Stretch if dual
        });

        // If adding as 2nd task, apply penalty to existing blueprint
        if (willBeDual) {
            const activeBlueprints = gameState.getActiveBlueprints();
            if (activeBlueprints.length > 0) {
                this.applyDualPenaltyToExisting(0);
            }
        }

        logger.game(`Started building ${milestone.name}`);
        return { success: true };
    }

    /**
     * Tick the active milestone (called from game tick)
     */
    tickMilestone() {
        const activeMilestone = gameState.getActiveMilestone();
        if (!activeMilestone) return;

        const milestone = Object.values(MILESTONES).find(m => m.id === activeMilestone.milestoneId);
        if (!milestone) return;

        const elapsed = Date.now() - activeMilestone.startedAt;
        const progress = Math.min(elapsed / activeMilestone.duration, 1);

        if (progress >= 1) {
            this.completeMilestone(milestone);
        }
    }

    /**
     * Complete a milestone
     */
    completeMilestone(milestone) {
        // If milestone was running alongside a blueprint, remove penalty from blueprint
        const usedSlots = this.getUsedContractorSlots();
        if (usedSlots === 2) {
            const activeBlueprints = gameState.getActiveBlueprints();
            if (activeBlueprints.length > 0) {
                this.removeDualPenaltyFromRemaining(0);
            }
        }

        // Award XP
        const xpResult = addXP(SKILLS.ARCHITECTING, milestone.xp, milestone.id);

        // Mark as completed
        gameState.completeMilestone(milestone.id);

        // Clear active milestone
        gameState.clearActiveMilestone();

        // Guarantee a civic contract spawn (bad luck protection)
        this.rollForCivicContract(true);

        if (this.onMilestoneComplete) {
            this.onMilestoneComplete(milestone, xpResult);
        }

        logger.game(`Completed ${milestone.name}! ${milestone.description}`);
        if (xpResult.leveledUp) {
            logger.game(`Architecting level up! Now level ${xpResult.newLevel}`);
        }
    }

    /**
     * Stop the active milestone (cancels it)
     */
    stopMilestone() {
        const usedSlots = this.getUsedContractorSlots();

        // If going from 2 tasks to 1, remove penalty from remaining blueprint
        if (usedSlots === 2) {
            const activeBlueprints = gameState.getActiveBlueprints();
            if (activeBlueprints.length > 0) {
                this.removeDualPenaltyFromRemaining(0);
            }
        }

        gameState.clearActiveMilestone();
    }

    /**
     * Get milestone progress (0-1)
     */
    getMilestoneProgress() {
        const activeMilestone = gameState.getActiveMilestone();
        if (!activeMilestone) return null;

        const elapsed = Date.now() - activeMilestone.startedAt;
        return {
            milestoneId: activeMilestone.milestoneId,
            progress: Math.min(elapsed / activeMilestone.duration, 1)
        };
    }

    // ==================== CIVIC CONTRACT METHODS ====================

    /**
     * Check if a civic contract can be started
     */
    canStartCivicContract(contractId) {
        const contract = Object.values(CIVIC_CONTRACTS).find(c => c.id === contractId);
        if (!contract) return { success: false, reason: 'Contract not found' };

        // Contractor required for civic contracts
        if (!this.hasContractor()) {
            return { success: false, reason: 'Hire a contractor first (complete "The Carpenter\'s Arrival" quest)' };
        }

        // Check contractor slot availability (civic contracts use a slot during build phases)
        const usedSlots = this.getUsedContractorSlots();
        const maxSlots = this.getContractorSlotCount();
        if (usedSlots >= maxSlots) {
            return { success: false, reason: 'All contractor slots are in use. Stop a blueprint or milestone first.' };
        }

        // Must be in available pool
        if (!gameState.hasCivicContractAvailable(contractId)) {
            return { success: false, reason: 'Contract not available' };
        }

        // Already have an active contract?
        const activeContract = gameState.getActiveCivicContract();
        if (activeContract) {
            return { success: false, reason: 'Already have an active civic contract' };
        }

        const skill = gameState.getSkill(SKILLS.ARCHITECTING);
        if (skill.level < contract.level) {
            return { success: false, reason: `Requires level ${contract.level} Architecting` };
        }

        // Check first phase requirements
        const firstPhase = contract.phases[0];
        if (firstPhase.type === 'build') {
            // Check gold
            if (firstPhase.gold > 0) {
                const playerGold = gameState.getGold();
                if (playerGold < firstPhase.gold) {
                    return { success: false, reason: `Phase 1 needs ${firstPhase.gold} gold` };
                }
            }

            // Check materials
            const missingMaterials = [];
            for (const [itemId, qty] of Object.entries(firstPhase.materials)) {
                const playerQty = gameState.getItemCount(itemId);
                if (playerQty < qty) {
                    const item = getItem(itemId);
                    const itemName = item ? item.name : itemId;
                    missingMaterials.push(`${itemName}: ${playerQty}/${qty}`);
                }
            }

            if (missingMaterials.length > 0) {
                return { success: false, reason: `Phase 1 missing: ${missingMaterials.join(', ')}` };
            }
        }

        return { success: true };
    }

    /**
     * Start a civic contract
     */
    startCivicContract(contractId) {
        const contract = Object.values(CIVIC_CONTRACTS).find(c => c.id === contractId);
        const canStart = this.canStartCivicContract(contractId);

        if (!canStart.success) {
            return canStart;
        }

        const firstPhase = contract.phases[0];

        // Consume gold and materials for first phase
        if (firstPhase.type === 'build') {
            if (firstPhase.gold > 0) {
                gameState.removeItem('coins', firstPhase.gold);
            }
            for (const [itemId, qty] of Object.entries(firstPhase.materials)) {
                gameState.removeItem(itemId, qty);
            }
        }

        // Remove from available contracts pool
        gameState.removeAvailableCivicContract(contractId);

        // Set active contract
        gameState.setActiveCivicContract({
            contractId: contractId,
            currentPhase: 0,
            phaseStartedAt: Date.now(),
            status: firstPhase.type === 'wait' ? 'waiting' : 'building'
        });

        logger.game(`Started civic contract: ${contract.name}`);
        return { success: true };
    }

    /**
     * Check if we can start the next phase
     */
    canStartNextPhase() {
        const activeContract = gameState.getActiveCivicContract();
        if (!activeContract) return { success: false, reason: 'No active contract' };

        const contract = Object.values(CIVIC_CONTRACTS).find(c => c.id === activeContract.contractId);
        if (!contract) return { success: false, reason: 'Contract not found' };

        const nextPhaseIndex = activeContract.currentPhase + 1;
        if (nextPhaseIndex >= contract.phases.length) {
            return { success: false, reason: 'No more phases' };
        }

        const nextPhase = contract.phases[nextPhaseIndex];
        if (nextPhase.type !== 'build') {
            return { success: true }; // Wait phases don't require materials or contractor
        }

        // Build phases need a contractor slot
        const usedSlots = this.getUsedContractorSlots();
        const maxSlots = this.getContractorSlotCount();
        if (usedSlots >= maxSlots) {
            return { success: false, reason: 'All contractor slots are in use. Stop a blueprint or milestone first.' };
        }

        // Check gold
        if (nextPhase.gold > 0) {
            const playerGold = gameState.getGold();
            if (playerGold < nextPhase.gold) {
                return { success: false, reason: `Need ${nextPhase.gold} gold` };
            }
        }

        // Check materials
        const missingMaterials = [];
        for (const [itemId, qty] of Object.entries(nextPhase.materials)) {
            const playerQty = gameState.getItemCount(itemId);
            if (playerQty < qty) {
                const item = getItem(itemId);
                const itemName = item ? item.name : itemId;
                missingMaterials.push(`${itemName}: ${playerQty}/${qty}`);
            }
        }

        if (missingMaterials.length > 0) {
            return { success: false, reason: `Missing: ${missingMaterials.join(', ')}` };
        }

        return { success: true };
    }

    /**
     * Start the next phase of active civic contract
     */
    startNextPhase() {
        const activeContract = gameState.getActiveCivicContract();
        if (!activeContract) return { success: false, reason: 'No active contract' };

        const canStart = this.canStartNextPhase();
        if (!canStart.success) return canStart;

        const contract = Object.values(CIVIC_CONTRACTS).find(c => c.id === activeContract.contractId);
        const nextPhaseIndex = activeContract.currentPhase + 1;
        const nextPhase = contract.phases[nextPhaseIndex];

        // Consume materials for build phases
        if (nextPhase.type === 'build') {
            if (nextPhase.gold > 0) {
                gameState.removeItem('coins', nextPhase.gold);
            }
            for (const [itemId, qty] of Object.entries(nextPhase.materials)) {
                gameState.removeItem(itemId, qty);
            }
        }

        // Update contract state
        gameState.updateActiveCivicContract({
            currentPhase: nextPhaseIndex,
            phaseStartedAt: Date.now(),
            status: nextPhase.type === 'wait' ? 'waiting' : 'building'
        });

        logger.game(`Started phase: ${nextPhase.name}`);
        return { success: true };
    }

    /**
     * Tick the active civic contract (called from game tick)
     */
    tickCivicContract() {
        const activeContract = gameState.getActiveCivicContract();
        if (!activeContract) return;

        // Don't process if waiting for player to start next phase
        if (activeContract.status === 'ready_for_next') return;

        const contract = Object.values(CIVIC_CONTRACTS).find(c => c.id === activeContract.contractId);
        if (!contract) return;

        const currentPhase = contract.phases[activeContract.currentPhase];
        const elapsed = Date.now() - activeContract.phaseStartedAt;
        const progress = Math.min(elapsed / currentPhase.duration, 1);

        if (progress >= 1) {
            // Award XP for this phase
            if (currentPhase.xp > 0) {
                addXP(SKILLS.ARCHITECTING, currentPhase.xp, `${contract.id}_phase_${activeContract.currentPhase}`);
            }

            // Check if this was the last phase
            if (activeContract.currentPhase >= contract.phases.length - 1) {
                this.completeCivicContract(contract);
            } else {
                // Move to next phase
                const nextPhaseIndex = activeContract.currentPhase + 1;
                const nextPhase = contract.phases[nextPhaseIndex];

                if (nextPhase.type === 'wait') {
                    // Auto-start wait phases
                    gameState.updateActiveCivicContract({
                        currentPhase: nextPhaseIndex,
                        phaseStartedAt: Date.now(),
                        status: 'waiting'
                    });
                } else {
                    // Build phase - set to ready and wait for player
                    gameState.updateActiveCivicContract({
                        currentPhase: activeContract.currentPhase,
                        status: 'ready_for_next'
                    });
                }

                if (this.onCivicPhaseComplete) {
                    this.onCivicPhaseComplete(contract, currentPhase, activeContract.currentPhase);
                }
            }
        }
    }

    /**
     * Complete a civic contract
     */
    completeCivicContract(contract) {
        // Talent: Civic Pride - bonus gold from contracts
        const goldBonus = getTalentFlatBonus(SKILLS.ARCHITECTING, 'contractGoldBonus');
        const bonusGoldMultiplier = 1 + goldBonus;
        const totalGold = Math.floor(contract.rewards.gold * bonusGoldMultiplier);

        // Talent: Valued Contractor - bonus XP from contracts
        const xpBonus = getTalentFlatBonus(SKILLS.ARCHITECTING, 'contractXPBonus');
        const bonusXPMultiplier = 1 + xpBonus;
        const totalXP = Math.floor(contract.rewards.xp * bonusXPMultiplier);

        // Award completion rewards
        if (totalGold > 0) {
            gameState.addItem('coins', totalGold);
        }

        if (totalXP > 0) {
            addXP(SKILLS.ARCHITECTING, totalXP, `${contract.id}_completion`);
        }

        // Award items
        if (contract.rewards.items && contract.rewards.items.length > 0) {
            for (const reward of contract.rewards.items) {
                gameState.addItem(reward.itemId, reward.quantity);
            }
        }

        // Record completion
        gameState.completeCivicContract(contract.id);

        // Clear active contract
        gameState.clearActiveCivicContract();

        const bonusText = goldBonus > 0 ? ` (+${Math.round(goldBonus * 100)}% bonus)` : '';
        logger.game(`Completed civic contract: ${contract.name}! Earned ${totalGold} gold${bonusText}`);
    }

    /**
     * Get civic contract progress
     */
    getCivicContractProgress() {
        const activeContract = gameState.getActiveCivicContract();
        if (!activeContract) return null;

        const contract = Object.values(CIVIC_CONTRACTS).find(c => c.id === activeContract.contractId);
        if (!contract) return null;

        const currentPhase = contract.phases[activeContract.currentPhase];
        const elapsed = Date.now() - activeContract.phaseStartedAt;

        return {
            contractId: activeContract.contractId,
            currentPhase: activeContract.currentPhase,
            totalPhases: contract.phases.length,
            phaseName: currentPhase.name,
            phaseType: currentPhase.type,
            status: activeContract.status,
            progress: Math.min(elapsed / currentPhase.duration, 1)
        };
    }

    // ==================== PASSIVE XP TALENT METHODS ====================
    // These are called by other systems when talent-related events occur

    /**
     * Get passive XP multiplier from Realm Steward capstone
     */
    getPassiveXPMultiplier() {
        const bonus = getTalentFlatBonus(SKILLS.ARCHITECTING, 'passiveXPBonus');
        return 1 + bonus; // 1.0 base, +0.5 with capstone
    }

    /**
     * Award XP when auto-eat consumes food (Kitchen Overseer talent)
     * Called from combat system's auto-eat
     */
    awardAutoEatXP() {
        // Check if Mess Hall milestone is built
        if (!gameState.hasMilestone('mess_hall')) return 0;

        // Get talent bonus (uses scaling: 15/25/40)
        const baseXP = this.getScalingTalentBonus('autoEatXP', [15, 25, 40]);
        if (baseXP <= 0) return 0;

        // Apply Realm Steward bonus
        const totalXP = Math.floor(baseXP * this.getPassiveXPMultiplier());
        if (totalXP > 0) {
            addXP(SKILLS.ARCHITECTING, totalXP, 'autoeat_passive', { skipTracking: true, skipCorruption: true });
        }
        return totalXP;
    }

    /**
     * Award XP when claiming from guild chest (Guild Liaison talent)
     * Called from pilfering system
     */
    awardGuildChestXP() {
        // Check if Guild Hall milestone is built
        if (!gameState.hasMilestone('guild_hall')) return 0;

        // Get talent bonus (uses scaling: 20/35/50)
        const baseXP = this.getScalingTalentBonus('guildChestXP', [20, 35, 50]);
        if (baseXP <= 0) return 0;

        // Apply Realm Steward bonus
        const totalXP = Math.floor(baseXP * this.getPassiveXPMultiplier());
        if (totalXP > 0) {
            addXP(SKILLS.ARCHITECTING, totalXP, 'guildchest_passive', { skipTracking: true, skipCorruption: true });
        }
        return totalXP;
    }

    /**
     * Award XP when claiming from pet chest (Beast Keeper talent)
     * Called from husbandry system
     */
    awardPetChestXP() {
        // Check if Pet Quarters milestone is built
        if (!gameState.hasMilestone('pet_quarters_i')) return 0;

        // Get talent bonus (uses scaling: 20/35/50)
        const baseXP = this.getScalingTalentBonus('petChestXP', [20, 35, 50]);
        if (baseXP <= 0) return 0;

        // Apply Realm Steward bonus
        const totalXP = Math.floor(baseXP * this.getPassiveXPMultiplier());
        if (totalXP > 0) {
            addXP(SKILLS.ARCHITECTING, totalXP, 'petchest_passive', { skipTracking: true, skipCorruption: true });
        }
        return totalXP;
    }

    /**
     * Award XP when using BloomHold (Realm Steward capstone)
     * Called from farming system
     */
    awardBloomholdXP() {
        // Check if Greenhouse milestone is built
        if (!gameState.hasMilestone('greenhouse')) return 0;

        // Get talent bonus (flat 10 XP from capstone)
        const baseXP = getTalentFlatBonus(SKILLS.ARCHITECTING, 'bloomholdXP');
        if (baseXP <= 0) return 0;

        // Note: Don't apply Realm Steward multiplier here - it's part of the same capstone
        if (baseXP > 0) {
            addXP(SKILLS.ARCHITECTING, baseXP, 'bloomhold_passive', { skipTracking: true, skipCorruption: true });
        }
        return baseXP;
    }

    /**
     * Helper to get scaling talent bonus value based on rank
     * Scaling talents have different values per rank (e.g., 15/25/40)
     */
    getScalingTalentBonus(bonusType, scalingValues) {
        // For scaling talents, we need to figure out the current rank
        // The flat bonus system returns the value, but for scaling we need the actual rank
        // Check talent tree directly
        const talentTree = gameState.getTalentTree(SKILLS.ARCHITECTING);
        if (!talentTree || !talentTree.nodes) return 0;

        // Find the node with this bonus type
        const nodeId = this.findNodeByBonusType(bonusType);
        if (!nodeId) return 0;

        const rank = talentTree.nodes[nodeId] || 0;
        if (rank <= 0 || rank > scalingValues.length) return 0;

        return scalingValues[rank - 1];
    }

    /**
     * Find the talent node ID that provides a specific bonus type
     */
    findNodeByBonusType(bonusType) {
        const bonusToNode = {
            'autoEatXP': 'arch_kitchen',
            'guildChestXP': 'arch_guild',
            'petChestXP': 'arch_beast'
        };
        return bonusToNode[bonusType] || null;
    }

    /**
     * Reroll any uncommon civic contracts when Grand Architect talent is unlocked.
     * Removes uncommon contracts from available pool. If the active contract is uncommon
     * but already in progress (currentPhase > 0), let it finish — don't waste their mats/XP.
     */
    rerollUncommonContracts() {
        let upgraded = 0;
        const level = gameState.getSkill(SKILLS.ARCHITECTING).level;

        // Helper: find a rare+ contract eligible for this player that isn't already in the pool
        const findReplacement = () => {
            const currentPool = gameState.getAvailableCivicContracts();
            const activeId = gameState.getActiveCivicContract()?.contractId;
            const eligible = Object.values(CIVIC_CONTRACTS).filter(c =>
                c.rarity !== 'uncommon' &&
                level >= c.level &&
                !currentPool.includes(c.id) &&
                c.id !== activeId
            );
            if (eligible.length > 0) {
                return eligible[Math.floor(Math.random() * eligible.length)];
            }
            // Fallback: allow duplicates of rare+ contracts rather than losing the slot
            const fallback = Object.values(CIVIC_CONTRACTS).filter(c =>
                c.rarity !== 'uncommon' && level >= c.level
            );
            return fallback.length > 0 ? fallback[Math.floor(Math.random() * fallback.length)] : null;
        };

        // Upgrade active contract if uncommon and not in progress
        const active = gameState.getActiveCivicContract();
        if (active) {
            const activeContract = Object.values(CIVIC_CONTRACTS).find(c => c.id === active.contractId);
            if (activeContract && activeContract.rarity === 'uncommon') {
                if (active.currentPhase === 0) {
                    const replacement = findReplacement();
                    if (replacement) {
                        gameState.clearActiveCivicContract();
                        gameState.addAvailableCivicContract(replacement.id);
                        upgraded++;
                        logger.game(`Upgraded uncommon contract to ${replacement.rarity}: ${replacement.name}`);
                    }
                } else {
                    logger.game(`${activeContract.name} is in progress — it will finish, then only rare+ contracts will spawn.`);
                }
            }
        }

        // Upgrade uncommon contracts in available pool
        const available = [...gameState.getAvailableCivicContracts()];
        for (const contractId of available) {
            const contract = Object.values(CIVIC_CONTRACTS).find(c => c.id === contractId);
            if (contract && contract.rarity === 'uncommon') {
                gameState.removeAvailableCivicContract(contractId);
                const replacement = findReplacement();
                if (replacement) {
                    gameState.addAvailableCivicContract(replacement.id);
                    upgraded++;
                    logger.game(`Upgraded uncommon contract to ${replacement.rarity}: ${replacement.name}`);
                }
            }
        }

        if (upgraded > 0) {
            logger.game(`Grand Architect: ${upgraded} contract(s) upgraded to rare+!`);
        }
    }

}

export const architectingSkill = new ArchitectingSkill();

// When Grand Architect talent is unlocked, immediately reroll uncommon contracts
onTalentUnlocked((skillId, nodeId) => {
    if (skillId === SKILLS.ARCHITECTING && nodeId === 'arch_grand') {
        architectingSkill.rerollUncommonContracts();
    }
});

export default architectingSkill;
